/**
 * Writing a CSV in the browser.
 *
 * Several screens offer an Export that no endpoint serves — there is no
 * `GET /platform/businesses/export`, no access-log export and no statement
 * route. Rather than leave those buttons as toasts that lie, they write the
 * rows already on screen. That is a real, if smaller, thing: it exports the
 * filtered view the reader is looking at, and nothing else.
 *
 * Extracted from `HistoryScreen` so the quoting and the BOM are decided once.
 * Both are load-bearing and both are easy to get wrong:
 *
 * - **Every cell is quoted**, always. A comma in a business name or a note
 *   would otherwise shift every column after it, and the file would still open
 *   — just wrong, which is the worst way for this to fail.
 * - **The file starts with a BOM.** It is what makes Excel read Urdu names and
 *   the rupee sign as UTF-8 instead of mojibake. Without it the export is
 *   technically correct and unusable.
 */

/** One CSV cell. Quoted always; embedded quotes doubled per RFC 4180. */
export const cell = (value: string | number | undefined | null): string =>
  value === undefined || value === null ? '""' : `"${String(value).replace(/"/g, '""')}"`

/**
 * Turn a header row and body rows into a file the browser downloads.
 *
 * `filename` is given without an extension; the caller usually wants the date
 * in it, because two exports of a moving list are two different files.
 */
export function downloadCsv(
  filename: string,
  header: (string | number | undefined)[],
  rows: (string | number | undefined | null)[][],
): void {
  const lines = [header.map(cell), ...rows.map((row) => row.map(cell))]
  const body = lines.map((line) => line.join(',')).join('\r\n')

  const blob = new Blob([`﻿${body}`], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${filename}.csv`
  anchor.click()
  URL.revokeObjectURL(url)
}
